/// <reference types="vite-plugin-svgr/client" />
import { notify,
	type CloudFlareResult,
	type Message,
	type ProcessFileType,
	StyledPillButton,
	clientConfig,
	colors,
	getFileType,
	getSdk,
	measures,
	strings,
	text,
} from "@quickpoint.me/shared";
import { getAuth, getIdToken } from "firebase/auth";
import { useEffect, useState } from "react";
import { useRecoilCallback, useRecoilState, useRecoilValue } from "recoil";
import { uploadImageToCloudflare } from "../../lib/uploadImageToCloudflare";
import {
	HostId,
	ProcessFileFamily,
} from "../../state/uploaderState";

import type { QpFile } from "@quickpoint.me/shared/src/gql/generated/schema";
import { GraphQLClient } from "graphql-request";
import { cloneDeep } from "lodash-es";
import { useForm } from "react-hook-form";
import styled from "styled-components";
import Countdown from "../../icons/countdown.svg?react";
import { spinAnimation } from "../../lib/animations";
import {
	convertExcelUpload,
	isExcelUpload,
	isMislabelledCsv,
} from "../../lib/convertExcelToCsv";
import { uploadDatafileToCloudflare } from "../../lib/uploadDataFileToCloudflare";
import { generateFileUrl } from "../../lib/utils";

interface FormValue {
	userDefinedName: string;
	altText: string;
}

const StyledFile = styled.div<{
	$uploading: boolean;
	$uploaded: boolean;
	$isInPortrait: boolean;
}>`
    border: 1px solid ${(props) => props.theme.navigationFrame};
    background: white;
    font-family: HelveticaNeue, sans-serif;
    border-radius: 1svw;
   
    position: relative;
    opacity: 1;
    z-index: 1;
    .upload-button{
        cursor: pointer;
    }
    .form-grid{
        display: grid;
        padding: ${(props) =>
					props.$isInPortrait
						? measures.dialogPanel.portrait.margin
						: measures.dialogPanel.landscape.margin};
        width: 100%;
        height: 20svh;
        grid-template: 
            "img name"
            "img alt"
            "img submit";
         grid-template-columns: 16svh 1fr;
         grid-template-rows: auto;
         grid-gap: 1svw;
    }
    .image-preview-holder {
        grid-area: img;
        border: 1px solid ${(props) => props.theme.navigationFrame};
        border-radius: .5svw;
        display: flex;
        align-items: center;
        aspect-ratio: 1/1;
        width: 16svh;
        height: 16svh;
        img {
            object-fit: contain;
            max-width: 100%;
            max-height: 100%;
        }

    }
    
    .form-input-user-defined-name {
      grid-area: name;
      input {
        font-family: ${(props) => props.theme.font};
        padding: .5svw;
        border: 1px solid ${(props) => props.theme.navigationFrame};
        border-radius: .5svw;
        width: 100%
        }
     

    }
    
    .form-input-alt-text {
        grid-area: alt;
        input {
        font-family: ${(props) => props.theme.font};
        padding: .5svw;
        border: 1px solid ${(props) => props.theme.navigationFrame};
        border-radius: .5svw;
        width: 100%
        }
    }

     .form-input-submit{
        grid-area: submit;
    }

    .form-error {
        color: ${colors.textSafe.red}; /* 7.5:1 (AAA) — the old #FF6961 was 2.3:1 */
        font-style: italic;
        font-size: .8rem;
        padding-inline-start: .5rem;
    }


`;

export const StyledUploading = styled.div`
    background: ${(props) => props.theme.backgroundColor};
    border-radius: .5rem;
    border: black 1px solid;
    height: 100%;
    left: -0.1rem;
    position: absolute;
    top: -0.1rem;
    width: 100%;
    .countdown {
        animation: ${spinAnimation};
        height: 100%;
        width: 100%;
        
    }
    
`;

function UploadFileView({
	fileID,
	$isInPortrait,
}: { fileID: string; $isInPortrait: boolean }) {
	const [file, setFile] = useRecoilState(ProcessFileFamily(fileID));
	const client = new GraphQLClient(clientConfig.gqlWriteFile());
	const {
		register,
		watch,
		handleSubmit,
		formState: { errors },
	} = useForm<FormValue>();
	const [uploading, setUploading] = useState(false);
	const hostId = useRecoilValue(HostId);

	if (file?.fileType === undefined) {
		throw new Error(
			"fileType is undefined. Cannot upload file without a valid fileType.",
		);
	}
	const fileType = getFileType(file.fileType);

	useEffect(() => {
		const subscription = watch(({ userDefinedName, altText }) => {
			setFile((prev) => {
				const newVal = cloneDeep(prev);
				if (!newVal) return prev;
				if (userDefinedName) newVal.userDefinedName = userDefinedName;
				if (altText) newVal.altText = altText;
				return newVal;
			});
		});
		return () => {
			subscription.unsubscribe();
		};
	}, [watch, setFile]);

	// 0. Excel files are converted to CSV before upload — the viewer charts
	// from text only. A .csv the browser mislabelled as vnd.ms-excel just
	// gets its mime type corrected.
	useEffect(() => {
		if (!file || file.cloudFlarePromise || file.cloudFlareResult) return;
		if (isMislabelledCsv(file)) {
			setFile((prev) => (prev ? { ...prev, fileType: "text/csv" } : prev));
			return;
		}
		if (!isExcelUpload(file)) return;
		let cancelled = false;
		(async () => {
			try {
				const converted = await convertExcelUpload(file);
				if (!cancelled) setFile(() => converted);
			} catch (e) {
				console.error("Excel conversion failed", e);
				if (!cancelled) {
					notify(text.fileUploader.excelConvertError);
				}
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [file, setFile]);

	// 1. Start upload to Cloudflare as soon as file is added
	useEffect(() => {
		if (!file || file.cloudFlarePromise || file.cloudFlareResult) return;
		// wait for the Excel→CSV conversion effect to replace the file first
		if (isExcelUpload(file) || isMislabelledCsv(file)) return;
		let cancelled = false;

		const uploadPromise: Promise<CloudFlareResult> = (async () => {
			try {
				const user = getAuth().currentUser;
				if (!user) throw new Error("No user");
				const jwt = await getIdToken(user);

				let decoded: CloudFlareResult | undefined;
				if (fileType === "image") {
					decoded = await uploadImageToCloudflare({
						imageFile: file,
						validCloudFlareUploadUrl: false,
						jwt,
					});
				}
				if (fileType === "data" || fileType === "audio") {
					decoded = await uploadDatafileToCloudflare({
						dataFile: file,
						jwt,
					});
				}
				if (!decoded) {
					throw new Error("Cloudflare upload did not return a result");
				}

				if (!cancelled && decoded) {
					setFile((oldVal) => {
						const newVal = cloneDeep(oldVal);
						if (!newVal) return oldVal;
						newVal.cloudFlareResult = decoded;
						return newVal;
					});
				}
				return decoded;
			} catch (e) {
				console.error("Cloudflare upload failed", e);
				if (!cancelled) {
					notify(
						e instanceof Error && e.message === "storage-limit"
							? text.tkns.freeLimitStorage
							: "There was a problem uploading your file. Please try again.",
					);
				}
				throw e;
			}
		})();

		setFile((oldVal) => {
			const newVal = cloneDeep(oldVal);
			if (!newVal) return oldVal;
			newVal.cloudFlarePromise = uploadPromise;
			return newVal;
		});

		return () => {
			cancelled = true;
		};
	}, [
		file,
		fileType,
		setFile,
	]);

	const uploadFile = useRecoilCallback(
		({ set }) =>
			async (id: string, ownerName: string, fileToUpload: ProcessFileType) => {
				const user = getAuth().currentUser;
				if (!user) return;
				const jwt = await getIdToken(user);
				if (!file) {
					console.error("No File?");
					return;
				}
				setUploading(true);

				let decoded: CloudFlareResult | undefined;
				try {
					// 2. Wait for upload to Cloudflare if not already done
					if (file.cloudFlareResult) {
						decoded = file.cloudFlareResult;
					} else if (file.cloudFlarePromise) {
						decoded = await file.cloudFlarePromise;
					} else {
						// Should not happen, but just in case
						console.error("No Cloudflare upload in progress");
						setUploading(false);
						return;
					}
				} catch (e) {
					setUploading(false);
					// Error already alerted in upload effect
					return;
				}

				const url = generateFileUrl(fileType, decoded.result.id);

				const map: QpFile = {
					altText: fileToUpload.altText,
					cloudFlareName: decoded.result.filename,
					fileName: fileToUpload.fileName,
					fileType: fileToUpload.fileType,
					id: decoded.result.id,
					ownerName,
					tags: [],
					uploadedOn: new Date().getTime(),
					url,
					userDefinedName: fileToUpload.userDefinedName.trim(),
					stackId: fileToUpload.stackId,
				};
				// storage accounting: the tier caps are enforced from these
				const bytes = fileToUpload.fileSize ?? 0;

				try {
					const toAuthorBC = new BroadcastChannel(
						strings.broadcastChannel.author,
					);
					const res = await getSdk(client).UpdateFile({ ...map, jwt, bytes });
					if (res.updateFile?.success) {
						const message: Message = {
							hostId,
							type: "fileUploader",
							action: strings.messages.updateFiles,
						};
						toAuthorBC.postMessage(message);
						setUploading(false);

						set(ProcessFileFamily(id), (oldVal) => {
							const newVal = cloneDeep(oldVal);
							if (!newVal) return oldVal;
							newVal.uploading = false;
							newVal.inUploadQ = false;
							newVal.uploadComplete = true;
							return newVal;
						});
						toAuthorBC.close();
					} else {
						notify(
							`Sorry there was a problem. Please try again:${res.updateFile?.message}`,
						);
						setUploading(false);
						const deleteImage = await getSdk(client).DeleteFile({
							fileId: decoded.result.id,
							jwt,
						});
						console.log(deleteImage);
						toAuthorBC.close();
					}
				} catch (err) {
					notify("Sorry there was a problem. Please try again");
					console.error(err);
					setUploading(false);
					const deleteImage = await getSdk(client).DeleteFile({
						fileId: decoded.result.id,
						jwt,
					});
					console.log(deleteImage);
				}
			},
		[fileID, file],
	);

	const fileSrc =
		fileType === "image"
			? file?.data
				? URL.createObjectURL(file.data)
				: ""
			: fileType === "data"
				? "/Files.svg"
				: "/Music.svg";

	const onSubmit = () => {
		if (!file) return;
		uploadFile(fileID, file.ownerName, file);
	};

	const triggerSubmit = () => {
		handleSubmit(onSubmit)();
	};

	return (
		<StyledFile
			$uploading={uploading}
			$uploaded={!!file?.uploadComplete}
			$isInPortrait={$isInPortrait}
		>
			<form className="form-grid">
				<div className="image-preview-holder">
					<img
						className="file-view-image-preview"
						src={fileSrc}
						alt={`Preview for ${file?.altText ?? "Alt Text Required"}`}
					/>
				</div>

				<div className="form-input-user-defined-name">
					<input
						data-testid="user-defined-name-input"
						aria-label={text.fileUploader.nameLabel}
						placeholder={text.fileUploader.namePlaceholder}
						{...register("userDefinedName", { required: true })}
					/>
					{errors.userDefinedName && (
						<span className="form-error">{text.fileUploader.nameRequired}</span>
					)}
				</div>
				<div className="form-input-alt-text">
					<input
						data-testid="alt-text-input"
						aria-label={text.fileUploader.altLabel}
						placeholder={text.fileUploader.altPlaceholder(fileType)}
						{...register("altText", { required: true })}
					/>
					{errors.altText && (
						<span className="form-error">{text.fileUploader.altRequired}</span>
					)}
				</div>

				<div className="form-input-submit">
					<StyledPillButton
						data-testid="upload-button"
						onClick={triggerSubmit}
						className="upload-button"
					>
						{text.fileUploader.upload}
					</StyledPillButton>
				</div>
			</form>
			{uploading && (
				<StyledUploading>
					<Countdown className="countdown" />
				</StyledUploading>
			)}
		</StyledFile>
	);
}

export default UploadFileView;
